<template>
  <BaseDialog
    :open="open"
    :disabled="loading"
    @close="$emit('close')"
  >
    <template #title>
      Angebot abschließen?
    </template>
    <div class="dialog-body-inner">
      <p
        class="muted mt-0"
        style="font-size: 16px; color: var(--warn); font-weight: 500;"
      >
        Nach dem Abschließen sind keine Buchungen mehr möglich.
      </p>

      <p
        v-if="error"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ error }}
      </p>

      <p
        v-if="eventTitle"
        class="mt-12"
      >
        Angebot: <strong>{{ eventTitle }}</strong>
        <span class="muted"> ({{ eventMandatory ? 'Pflichtangebot' : 'freiwilliges Angebot' }})</span>
        <span
          v-if="eventTimesText"
          class="muted"
        > — {{ eventTimesText }}</span>
      </p>

      <p
        v-if="hasOpenCheckouts"
        class="notice notice-danger mt-12"
        role="alert"
      >
        Es gibt fehlende Austragungen (GEHEN). Bitte alle Austragungen durchführen, bevor das Angebot
        beendet werden kann.
      </p>

      <div
        class="mt-4"
        style="display:grid;grid-template-columns:1fr 1fr;gap:8px;"
      >
        <div>
          <h3
            class="small muted"
            style="margin:0 0 6px 0;"
          >
            Zusammenfassung
          </h3>
          <ul style="list-style:none;padding:0;margin:0;display:grid;gap:6px;">
            <li><strong>{{ countPresent }}</strong> teilgenommen</li>
            <li><strong>{{ countAbsent }}</strong> fehlend</li>
            <li><strong>{{ countLate }}</strong> mit Fehlzeit (Summe: <strong>{{ totalLateText }}</strong>)</li>
            <li><strong>{{ countReleased }}</strong> entlassen</li>
            <li><strong>{{ countNotifs }}</strong> Meldung(en)</li>
          </ul>
        </div>
        <div>
          <h3
            class="small muted"
            style="margin:0 0 6px 0;"
          >
            fehlende Austragungen (GEHEN)
          </h3>
          <ul
            v-if="openCheckoutsList.length"
            style="list-style:none;padding:0;margin:0;display:grid;gap:4px;"
          >
            <li
              v-for="s in openCheckoutsList"
              :key="`oc-${s.id}`"
            >
              {{ s.name }}
            </li>
          </ul>
          <p
            v-else
            class="muted"
            style="margin:0;"
          >
            - keine
          </p>
        </div>
      </div>

      <div class="absence-list mt-4">
        <h3
          class="small muted"
          style="margin:0 0 6px 0;"
        >
          Zur Kontrolle (fehlend oder mit Fehlzeit - wird nur bei Pflichtangeboten gezählt)
        </h3>

        <!-- Tabelle statt Liste -->
        <div
          v-if="flaggedStudents.length"
          class="absence-table"
        >
          <div class="absence-table-header">
            <div>Schüler</div>
            <div>Information</div>
            <div style="text-align:center;">
              Fehlzeit ignorieren
            </div>
          </div>
          <div
            v-for="st in flaggedStudents"
            :key="`fl-${st.id}`"
            class="absence-table-row"
          >
            <div class="absence-name">
              <strong>{{ st.name }}</strong>
            </div>
            <div class="absence-info muted">
              <template v-if="st.absent">
                fehlend
              </template>
              <template v-if="!st.absent && st.lateText">
                Fehlzeit {{ st.lateText }}
              </template>
              <span
                v-if="st.notify"
                style="font-size: 0.7rem"
              >
                – Meldung vorhanden
                <em v-if="st.notifyStatus !== 'akzeptiert'">(in Prüfung)</em>
              </span>
            </div>
            <div class="absence-worthless-cell">
              <template v-if="st.lateSeconds > 0 && st.latenessId">
                <label class="chk-inline">
                  <input
                    v-model="worthlessState[st.latenessId].current"
                    type="checkbox"
                  >
                </label>
              </template>
              <template v-else>
                <span class="muted small">–</span>
              </template>
            </div>
          </div>
        </div>

        <p
          v-else
          class="muted"
          style="margin:0;"
        >
          - keine Auffälligkeiten
        </p>
      </div>

      <div
        class="mt-12"
        style="display:flex;gap:8px;justify-content:flex-end;"
      >
        <button
          class="btn btn-warn"
          :disabled="loading || hasOpenCheckouts"
          @click="finish"
        >
          <span
            v-if="loading"
            class="spinner"
            aria-hidden="true"
          /> Abschließen
        </button>
        <button
          class="btn"
          :disabled="loading"
          @click="$emit('close')"
        >
          Abbrechen
        </button>
      </div>
    </div>
  </BaseDialog>
</template>

<script setup>
import {computed, onMounted, ref, watch} from 'vue';
import BaseDialog from '@/components/BaseDialog.vue';
import api from '@/services/api';

// Props
const props = defineProps({
  open: {type: Boolean, default: false},
  eventId: {type: [Number, String], default: null},
  eventTitle: {type: String, default: null},
  eventMandatory: {type: Boolean, default: false},
  eventStartsAt: {type: [String, Date, null], default: null},
  eventEndsAt: {type: [String, Date, null], default: null}
});
const emit = defineEmits(['close', 'completed']);

const loading = ref(false);
const error = ref('');
const rows = ref([]); // [{id, name, present, left, latenessSeconds, reasonId, notifyAccepted, notifySubject}]
const worthlessState = ref({});

// baut Map latenessId => { initial:boolean, current:boolean }
function buildWorthlessStateFromRows() {
  const map = {};
  (rows.value || []).forEach(r => {
    const id = Number(r.latenessId);
    if (Number.isFinite(id) && id > 0 && Number(r.latenessSeconds) > 0) {
      const init = Boolean(r.latenessWorthless ?? false);
      map[id] = {initial: init, current: init};
    }
  });
  worthlessState.value = map;
}

// Zeitenanzeige (Berlin)
const eventTimesText = computed(() => {
  const s = props.eventStartsAt;
  const e = props.eventEndsAt;
  if (!s || !e) return '';
  const parseLocal = (val) => {
    if (val instanceof Date && !Number.isNaN(val.getTime())) {
      const dm = new Intl.DateTimeFormat('de-DE', {timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit'});
      const hm = new Intl.DateTimeFormat('de-DE', {
        timeZone: 'Europe/Berlin',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      const ymdFmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      return {ddmm: dm.format(val), hm: hm.format(val), ymd: ymdFmt.format(val)};
    }
    const str = String(val).trim();
    const m = /^(\d{4})-(\d{2})-(\d{2})\s(\d{2}):(\d{2})(?::\d{2})?$/.exec(str);
    if (m) {
      const [, y, mo, d, hh, mi] = m;
      return {ddmm: `${d}.${mo}.`, hm: `${hh}:${mi}`, ymd: `${y}-${mo}-${d}`};
    }
    const d = new Date(str);
    if (!Number.isNaN(d.getTime())) {
      const dm = new Intl.DateTimeFormat('de-DE', {timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit'});
      const hm = new Intl.DateTimeFormat('de-DE', {
        timeZone: 'Europe/Berlin',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      const ymdFmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      return {ddmm: dm.format(d), hm: hm.format(d), ymd: ymdFmt.format(d)};
    }
    return null;
  };
  const sp = parseLocal(s);
  const ep = parseLocal(e);
  if (!sp || !ep) return '';
  const sameDay = sp.ymd === ep.ymd;
  return sameDay ? `${sp.hm} - ${ep.hm}` : `${sp.ddmm} ${sp.hm} - ${ep.ddmm} ${ep.hm}`;
});

// Helper
function secondsToHHMM(sec) {
  const n = Number(sec);
  if (!Number.isFinite(n) || n <= 0) return '00:00';
  const mins = Math.floor(n / 60);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// Summary-Computed
const totalStudents = computed(() => (rows.value || []).length);
const countPresent = computed(() => (rows.value || []).filter(r => r.present).length);
const countAbsent = computed(() => Math.max(0, totalStudents.value - countPresent.value));
const countLate = computed(() => (rows.value || []).filter(r => Number(r.latenessSeconds) > 0).length);
const totalLateSec = computed(() => (rows.value || []).reduce((sum, r) => sum + Number(r.latenessSeconds || 0), 0));
const totalLateText = computed(() => secondsToHHMM(totalLateSec.value));
const countReleased = computed(() => (rows.value || []).filter(r => r.reasonId || (r.reason && r.reason.id)).length);
const countNotifs = computed(() => (rows.value || []).filter(r => r.notifyAccepted).length);
const openCheckoutsList = computed(() => (rows.value || []).filter(r => r.present && !r.left));
const hasOpenCheckouts = computed(() => openCheckoutsList.value.length > 0);
const flaggedStudents = computed(() =>
    (rows.value || [])
        .filter(r => !r.present || Number(r.latenessSeconds) > 0)
        .map(r => ({
          id: r.id,
          name: r.name,
          absent: !r.present,
          lateSeconds: Number(r.latenessSeconds || 0),
          lateText: Number(r.latenessSeconds) > 0 ? secondsToHHMM(r.latenessSeconds) : '',
          notify: r.notifyAccepted,
          notifySubject: r.notifySubject,
          notifyStatus: r.notifyStatus,
          latenessId: r.latenessId ?? null,
          latenessWorthless: Boolean(r.latenessWorthless ?? false)
        }))
);

// Laden der Daten (Enrollments + Attendances + Notifications)
async function load() {
  if (!props.eventId || !props.open) return;
  loading.value = true;
  error.value = '';
  try {
    const res = await api.get(`/events/${props.eventId}/completion-summary`);
    const data = res?.data ?? {};

    // rows direkt aus dem Backend übernehmen
    rows.value = Array.isArray(data.rows) ? data.rows : [];
    buildWorthlessStateFromRows();

    // falls ihr die Summen aus dem Backend nutzen wollt, könnt ihr sie hier cachen,
    // oder wie bisher über computed aus rows.value berechnen.
  } catch (e) {
    error.value = 'Daten konnten nicht geladen werden.';
  } finally {
    loading.value = false;
  }
}

async function finish() {
  if (!props.eventId) return;
  loading.value = true;
  error.value = '';

  // Nur geänderte Checkboxen als Mapping { [latenessId]: boolean } sammeln
  const changes = {};
  (flaggedStudents.value || []).forEach(st => {
    if (st.lateSeconds > 0 && st.latenessId) {
      const id = Number(st.latenessId);
      if (!Number.isFinite(id)) return;
      const entry = worthlessState.value[id];
      if (entry && entry.current !== entry.initial) {
        changes[id] = !!entry.current;
      }
    }
  });

  try {
    await api.patch(`/events/${props.eventId}`, {
      status: 'completed',
      worthlessLatenessIds: changes
    });
    emit('completed');
  } catch (e) {
    error.value = 'Abschließen fehlgeschlagen.';
  } finally {
    loading.value = false;
  }
}

watch(() => props.open, (v) => {
  if (v) {
    load();
  } else {
    rows.value = [];
    error.value = '';
    worthlessState.value = {};
  }
});
watch(() => props.eventId, () => {
  if (props.open) load();
});

// Erstes Laden, falls schon offen
onMounted(() => {
  if (props.open) load();
});
</script>

<style scoped>
.muted {
  color: var(--muted);
}

.notice {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 10px 12px;
}

.notice-danger {
  background: rgba(239, 68, 68, 0.10);
  border-color: #FCA5A5;
}

.small {
  font-size: 12px;
  line-height: 16px;
}

.mt-12 {
  margin-top: 12px;
}

.btn {
  padding: .35rem .6rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}

.btn-warn {
  background: var(--warn);
  color: #fff;
  border-color: var(--warn);
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.dialog-body-inner {
  font-size: 13px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.absence-table {
  display: grid;
  grid-template-rows: auto;
  border: 1px solid var(--border);
  border-radius: 6px;
  overflow: hidden;
  font-size: 13px;
}

.absence-table-header {
  display: grid;
  grid-template-columns: 2fr 3fr 1.4fr;
  gap: 8px;
  padding: 6px 10px;
  background: var(--bg);
  color: var(--muted);
  font-size: 12px;
  font-weight: 500;
}

.absence-table-row {
  display: grid;
  grid-template-columns: 2fr 3fr 1.4fr;
  gap: 8px;
  padding: 6px 10px;
  border-top: 1px solid var(--border);
  align-items: center;
}

.absence-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.absence-info {
  overflow: hidden;
  text-overflow: ellipsis;
}

.absence-worthless-cell {
  text-align: center;
}

.chk-inline {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}
</style>
